import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, X, Search, ChevronDown, BookOpen, Trash2, Edit2, RefreshCw, TrendingUp, TrendingDown, Users } from 'lucide-react';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { MonthlyCashflowTable } from '../components/MonthlyCashflowTable';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TYPE_STYLE = {
  Debtor:   'bg-blue-900/40 text-blue-300 border-blue-700',
  Creditor: 'bg-purple-900/40 text-purple-300 border-purple-700',
  Both:     'bg-amber-900/40 text-amber-300 border-amber-700',
};

const EMPTY_FORM = {
  name: '', type: 'Debtor', gstin: '', address: '', city: '', state: 'Maharashtra',
  pin_code: '', phone: '', email: '', payment_terms: 'Net 30', credit_limit: '', notes: '',
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [ledgerClient, setLedgerClient] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerTab, setLedgerTab] = useState('entries');

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients/`, { withCredentials: true });
      setClients(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete, isAdmin } = useAdminDelete(fetchClients);

  useEffect(() => { fetchClients(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) {
        const res = await axios.put(`${API}/clients/${editing.id}`, form, { withCredentials: true });
        setClients(prev => prev.map(c => c.id === editing.id ? res.data : c));
      } else {
        const res = await axios.post(`${API}/clients/`, form, { withCredentials: true });
        setClients(prev => [...prev, res.data]);
      }
      setShowModal(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving client'); }
    finally { setSubmitting(false); }
  };

  const deleteClient = (id, name) => {
    requestDelete(`/clients/${id}`, name || 'Client', `Delete "${name || 'this client'}" permanently. All linked data will be affected.`);
  };

  const viewLedger = async (client) => {
    setLedgerClient(client);
    setLedgerLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${client.id}/ledger`, { withCredentials: true });
      setLedger(res.data);
    } catch (e) { toast.error('Error loading ledger'); }
    finally { setLedgerLoading(false); }
  };

  const filtered = clients.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.city?.toLowerCase().includes(search.toLowerCase()) ||
      c.gstin?.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'All' || c.type === filterType;
    return matchSearch && matchType;
  });

  const debtors = clients.filter(c => c.type === 'Debtor' || c.type === 'Both').length;
  const creditors = clients.filter(c => c.type === 'Creditor' || c.type === 'Both').length;

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading clients...</div>;

  return (
    <div className="space-y-5" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Client Master</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Sundry Debtors & Creditors — {clients.length} clients</p>
        </div>
        <button data-testid="add-client-btn" onClick={openAdd}
          className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
          <Plus size={14} /> New Client
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Clients', value: clients.length, icon: Users, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Sundry Debtors', value: debtors, icon: TrendingUp, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Sundry Creditors', value: creditors, icon: TrendingDown, color: 'text-purple-400', bg: 'bg-purple-500/10' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
              <Icon size={16} className={color} />
            </div>
            <div>
              <div className="text-zinc-400 text-xs">{label}</div>
              <div className="text-zinc-50 font-heading font-bold text-xl">{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            placeholder="Search by name, city, GSTIN..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-9 pr-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
            data-testid="client-search"
          />
        </div>
        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
          {['All', 'Debtor', 'Creditor', 'Both'].map(t => (
            <button key={t} onClick={() => setFilterType(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterType === t ? 'bg-amber-500 text-black font-semibold' : 'text-zinc-400 hover:text-zinc-200'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Client Name', 'Type', 'GSTIN', 'City', 'Phone', 'Payment Terms', 'Credit Limit', 'Actions'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`client-row-${c.id}`}>
                <td className="px-4 py-3">
                  <button onClick={() => viewLedger(c)}
                    className="text-amber-400 hover:text-amber-300 font-medium text-xs flex items-center gap-1 transition-colors">
                    {c.name}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_STYLE[c.type] || 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>{c.type}</span>
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{c.gstin || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{c.city || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{c.phone || '—'}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{c.payment_terms || '—'}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs font-mono">{c.credit_limit ? fmt(c.credit_limit) : '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => viewLedger(c)} title="View Ledger"
                      className="text-zinc-500 hover:text-amber-400 transition-colors" data-testid={`ledger-btn-${c.id}`}>
                      <BookOpen size={13} />
                    </button>
                    <button onClick={() => openEdit(c)} title="Edit"
                      className="text-zinc-500 hover:text-blue-400 transition-colors">
                      <Edit2 size={13} />
                    </button>
                    {isAdmin && <button onClick={() => deleteClient(c.id, c.name)} title="Delete"
                      data-testid={`delete-client-${c.id}`}
                      className="text-zinc-500 hover:text-red-400 transition-colors">
                      <Trash2 size={13} />
                    </button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-10">
            {search || filterType !== 'All' ? 'No clients match your filters' : 'No clients yet — add your first client'}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50">
                {editing ? 'Edit Client' : 'New Client'}
              </h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="client-form">
              {/* Type selector */}
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Client Type *</label>
                <div className="flex gap-2">
                  {['Debtor', 'Creditor', 'Both'].map(t => (
                    <button key={t} type="button"
                      onClick={() => setForm(f => ({ ...f, type: t }))}
                      className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.type === t ? TYPE_STYLE[t] : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}>
                      {t === 'Debtor' ? 'Sundry Debtor' : t === 'Creditor' ? 'Sundry Creditor' : 'Both'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Company / Client Name *</label>
                  <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. BigBasket Ltd." data-testid="client-name-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">GSTIN</label>
                  <input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))}
                    placeholder="27AABCX..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Phone</label>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+91 9876543210" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Email</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="accounts@client.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">City</label>
                  <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="Mumbai" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">State</label>
                  <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Address</label>
                  <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Full address" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Payment Terms</label>
                  <select value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {['Immediate', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Advance'].map(t => (
                      <option key={t} value={t} className="bg-zinc-900">{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Credit Limit (₹)</label>
                  <input type="number" min="0" value={form.credit_limit} onChange={e => setForm(f => ({ ...f, credit_limit: e.target.value }))}
                    placeholder="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                  <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm transition-colors">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="save-client-btn"
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors">
                  {submitting ? 'Saving...' : editing ? 'Update Client' : 'Add Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ledger Panel */}
      {ledgerClient && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-end z-50">
          <div className="h-full w-full max-w-2xl bg-zinc-950 border-l border-zinc-800 flex flex-col">
            {/* Ledger Header */}
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900">
              <div>
                <h3 className="font-heading font-semibold text-zinc-50">{ledgerClient.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_STYLE[ledgerClient.type]}`}>{ledgerClient.type}</span>
                  {ledgerClient.gstin && <span className="text-zinc-500 text-xs font-mono">{ledgerClient.gstin}</span>}
                </div>
              </div>
              <button onClick={() => { setLedgerClient(null); setLedger(null); }}
                className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
            </div>

            {/* Summary */}
            {ledger && (
              <div className="grid grid-cols-3 gap-3 p-4 border-b border-zinc-800">
                <div className="bg-zinc-900 rounded-xl p-3 text-center">
                  <div className="text-zinc-500 text-xs">Total Invoiced</div>
                  <div className="text-amber-400 font-mono font-bold text-sm mt-1">{fmt(ledger.total_invoiced)}</div>
                </div>
                <div className="bg-zinc-900 rounded-xl p-3 text-center">
                  <div className="text-zinc-500 text-xs">Total Paid/Credited</div>
                  <div className="text-green-400 font-mono font-bold text-sm mt-1">{fmt(ledger.total_paid)}</div>
                </div>
                <div className="bg-zinc-900 rounded-xl p-3 text-center">
                  <div className="text-zinc-500 text-xs">Outstanding ({ledger.balance_type})</div>
                  <div className={`font-mono font-bold text-sm mt-1 ${ledger.outstanding > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {fmt(Math.abs(ledger.outstanding))}
                  </div>
                </div>
              </div>
            )}

            {/* Ledger body: entries or monthly cash flow */}
            <div className="flex items-center gap-1 px-4 pt-2 border-b border-zinc-800 bg-zinc-950">
              {[['entries', 'Entries'], ['monthly', 'Monthly cash flow']].map(([k, l]) => (
                <button key={k} onClick={() => setLedgerTab(k)} data-testid={`ledger-tab-${k}`}
                  className={`px-3 py-1.5 text-xs rounded-t-lg border-b-2 -mb-px ${ledgerTab === k ? 'border-amber-400 text-amber-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
                  {l}
                </button>
              ))}
            </div>
            {ledgerTab === 'monthly' ? (
              <div className="flex-1 overflow-y-auto"><MonthlyCashflowTable clientId={ledgerClient.id} compact /></div>
            ) : (
            <div className="flex-1 overflow-y-auto">
              {ledgerLoading ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw size={18} className="animate-spin text-zinc-500" />
                </div>
              ) : ledger?.entries?.length === 0 ? (
                <div className="text-center text-zinc-500 text-sm py-10">No transactions found</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-zinc-950">
                    <tr className="border-b border-zinc-800">
                      {['Date', 'Type', 'Reference', 'Description', 'Debit (Dr)', 'Credit (Cr)', 'Balance'].map(h => (
                        <th key={h} className="text-left text-zinc-600 font-medium px-4 py-2.5">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(ledger?.entries || []).map((e, i) => (
                      <tr key={i} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                        <td className="px-4 py-2 text-zinc-500">{e.date}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                            e.type === 'Invoice' ? 'bg-blue-900/40 text-blue-300' :
                            e.type === 'Payment Received' ? 'bg-green-900/40 text-green-300' :
                            e.type === 'Purchase Bill' ? 'bg-purple-900/40 text-purple-300' :
                            'bg-zinc-800 text-zinc-400'
                          }`}>{e.type}</span>
                        </td>
                        <td className="px-4 py-2 font-mono text-amber-400/80 text-[10px]">{e.reference}</td>
                        <td className="px-4 py-2 text-zinc-400 max-w-[140px] truncate">{e.description}</td>
                        <td className="px-4 py-2 font-mono text-right">
                          {e.debit > 0 ? <span className="text-red-400">{fmt(e.debit)}</span> : <span className="text-zinc-700">—</span>}
                        </td>
                        <td className="px-4 py-2 font-mono text-right">
                          {e.credit > 0 ? <span className="text-green-400">{fmt(e.credit)}</span> : <span className="text-zinc-700">—</span>}
                        </td>
                        <td className="px-4 py-2 font-mono text-right">
                          <span className={e.running_balance >= 0 ? 'text-red-400' : 'text-green-400'}>
                            {fmt(Math.abs(e.running_balance))} {e.running_balance >= 0 ? 'Dr' : 'Cr'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            )}
          </div>
        </div>
      )}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />
    </div>
  );
}
